import React, { useEffect, useMemo, useState } from "react";
import {
  Check, Copy, ExternalLink, ChevronDown, PartyPopper, Sparkles,
  Mail, Phone, Video, MailOpen, Lightbulb, Star,
} from "lucide-react";
import { DAYS, PHOTO_CATEGORIES } from "../lib/wizardStore";

const PROGRESS_KEY = "gigma_publish_progress_v1";

function loadProgress() {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* ignore */ }
  return { done: {}, verifySel: [] };
}

function to12h(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

function formatHours(hours) {
  return DAYS.map((d) => {
    const h = hours[d];
    if (!h || h.closed || !h.open || !h.close) return `${d}: Closed`;
    return `${d}: ${to12h(h.open)} – ${to12h(h.close)}`;
  }).join("\n");
}

function generateDescription(state, bt) {
  const b = state.businessInfo;
  const cat = bt ? bt.category : "local business";
  const services = (state.services || "")
    .split(/\n|,|•/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 5);
  const parts = [];
  parts.push(`${b.name || "Our business"} is a ${cat.toLowerCase()}${b.serviceArea ? ` proudly serving ${b.serviceArea}` : " serving the local community"}.`);
  if (services.length) parts.push(`We specialize in ${services.join(", ").toLowerCase()}.`);
  parts.push("Our focus is quality work, honest service, and happy customers — every single visit.");
  if (b.phone) parts.push(`Call us at ${b.phone}${b.website ? ` or visit ${b.website}` : ""} to get started.`);
  return parts.join(" ").slice(0, 750);
}

export function CopyBox({ value, label, testId, multiline = false }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className="mt-2">
      {label && <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1">{label}</div>}
      <div className="flex items-stretch gap-2">
        <div className={`flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 font-medium ${multiline ? "whitespace-pre-wrap leading-relaxed" : "truncate"}`}>
          {value || <span className="text-slate-400 italic font-normal">Not provided in wizard</span>}
        </div>
        <button
          type="button"
          onClick={copy}
          disabled={!value}
          data-testid={testId}
          className={`shrink-0 inline-flex items-center gap-1.5 rounded-xl px-4 text-sm font-medium transition-colors ${
            copied ? "bg-emerald-500 text-white" : "bg-slate-900 text-white hover:bg-slate-700"
          } disabled:opacity-40`}
        >
          {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
        </button>
      </div>
    </div>
  );
}

function GoogleLinkButton({ href = "https://business.google.com/create", children, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="mt-3 inline-flex items-center gap-2 bg-blue-600 text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-blue-700 transition-colors"
    >
      {children} <ExternalLink className="w-4 h-4" />
    </a>
  );
}

export function Tip({ children }) {
  return (
    <div className="mt-3 flex gap-2 rounded-xl bg-amber-50 border border-amber-100 px-3.5 py-2.5 text-xs text-amber-800 leading-relaxed">
      <Lightbulb className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
      <span>{children}</span>
    </div>
  );
}

const VERIFY_OPTIONS = [
  { id: "email", label: "Email", icon: Mail, speed: "Instant", detail: "Google emails you a code — click it and you're verified within minutes. If you see this option, always pick it first." },
  { id: "phone", label: "Phone / SMS", icon: Phone, speed: "Instant", detail: "Google calls or texts a code to your listed number. Nearly instant — a great choice." },
  { id: "video", label: "Video", icon: Video, speed: "Same day", detail: "Record a short walkthrough showing your signage, equipment and proof you work there. Usually reviewed within 24 hours." },
  { id: "postcard", label: "Postcard", icon: MailOpen, speed: "5–14 days", detail: "Google mails a postcard with a 5-digit code to your address. Slowest option — only pick it if nothing else is offered. When it arrives, enter the code in Google." },
];

function VerificationHelper({ verifySel, toggleVerify }) {
  const selected = VERIFY_OPTIONS.filter((o) => verifySel.includes(o.id));
  const fastest = selected[0];
  return (
    <div>
      <p className="text-sm text-slate-600 leading-relaxed">
        Google will now ask you to verify you really own this business. Which options is Google showing you? Tap all that you see:
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {VERIFY_OPTIONS.map(({ id, label, icon: Icon, speed }) => {
          const active = verifySel.includes(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => toggleVerify(id)}
              data-testid={`verify-option-${id}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
                active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
              <span className={`text-[10px] ${active ? "text-slate-300" : "text-slate-400"}`}>({speed})</span>
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="verify-guidance">
          {fastest && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800">
              <strong>Our pick: {fastest.label}.</strong> {fastest.detail}
            </div>
          )}
          {selected.slice(1).map((o) => (
            <div key={o.id} className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 text-xs text-slate-600 leading-relaxed">
              <strong className="text-slate-800">{o.label} ({o.speed}):</strong> {o.detail}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PublishAssistant({ state, bt, onOpenReviews }) {
  const [progress, setProgress] = useState(loadProgress);
  const b = state.businessInfo;

  useEffect(() => {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  }, [progress]);

  const toggleVerify = (id) =>
    setProgress((p) => ({
      ...p,
      verifySel: p.verifySel.includes(id) ? p.verifySel.filter((v) => v !== id) : [...p.verifySel, id],
    }));

  const hoursText = useMemo(() => formatHours(state.hours), [state.hours]);
  const description = useMemo(() => generateDescription(state, bt), [state, bt]);
  const readyPhotos = PHOTO_CATEGORIES.filter((c) => state.photos[c.id] === "ready");

  const steps = [
    {
      title: "Open Google Business Profile",
      content: (
        <>
          <p className="text-sm text-slate-600 leading-relaxed">
            Google requires you to sign in with a Google account (Gmail) — that's how the profile becomes legally yours.
            Click below to open Google's official setup page in a new tab. <strong>Keep this tab open</strong> — every answer you'll need is right here with a Copy button.
          </p>
          <GoogleLinkButton testId="assistant-open-google">Open Google Setup</GoogleLinkButton>
        </>
      ),
    },
    {
      title: "Your name",
      why: "Your name is how people find and recognize you — Google matches it against searches.",
      content: (
        <>
          <p className="text-sm text-slate-600">Google asks for the name of your business, school, church or organization. Here's yours — just copy and paste:</p>
          <CopyBox value={b.name} testId="copy-business-name" />
          <Tip>If Google suggests an existing listing with your name, only claim it if it's genuinely yours.</Tip>
        </>
      ),
    },
    {
      title: "Your category",
      why: "Your category decides which searches you show up in — it's the #1 ranking factor.",
      content: (
        <>
          <p className="text-sm text-slate-600">Google asks what kind of place you are. Paste this, then pick the closest suggestion Google offers:</p>
          <CopyBox value={bt ? bt.category : ""} testId="copy-category" />
          <Tip>Always select from Google's dropdown suggestions — don't submit a custom category.</Tip>
        </>
      ),
    },
    {
      title: "Your location & service area",
      why: "This puts you on the map — literally. It's what makes you show up in 'near me' searches.",
      content: (
        <>
          <p className="text-sm text-slate-600">Paste your address when Google asks where people can visit you:</p>
          <CopyBox value={b.address} label="Address" testId="copy-address" />
          {b.serviceArea && (
            <>
              <p className="text-sm text-slate-600 mt-3">If Google asks whether you serve people at their location, answer Yes and add:</p>
              <CopyBox value={b.serviceArea} label="Service area" testId="copy-service-area" />
            </>
          )}
        </>
      ),
    },
    {
      title: "Your phone & website",
      why: "76% of local searches end in a phone call — make sure yours is one tap away.",
      content: (
        <>
          <CopyBox value={b.phone} label="Phone number" testId="copy-phone" />
          <CopyBox value={b.website} label="Website" testId="copy-website" />
        </>
      ),
    },
    {
      title: "Your hours",
      why: "Profiles with hours get 2x more visits — people check before they come.",
      content: (
        <>
          <p className="text-sm text-slate-600">Google shows hours day-by-day. Here's your full week — match each row exactly (copy for reference):</p>
          <CopyBox value={hoursText} multiline testId="copy-hours" />
        </>
      ),
    },
    {
      title: "Your description",
      why: "This is your pitch on Google — and we've already written it for you.",
      content: (
        <>
          <p className="text-sm text-slate-600">
            Most people freeze when Google asks them to "describe your business." Not you — Gigma wrote yours from your wizard answers ({description.length}/750 characters). Copy, paste, done:
          </p>
          <CopyBox value={description} multiline testId="copy-description" />
          <Tip>Feel free to tweak the wording after pasting — it's your voice, we just gave you a head start.</Tip>
        </>
      ),
    },
    {
      title: "Upload your photos",
      why: "Profiles with photos get 35% more clicks and 42% more direction requests.",
      content: (
        <>
          {readyPhotos.length > 0 ? (
            <>
              <p className="text-sm text-slate-600">You marked these photos as ready — upload them in Google's Photos section:</p>
              <ul className="mt-2 space-y-1.5">
                {readyPhotos.map((c) => (
                  <li key={c.id} className="flex items-center gap-2 text-sm text-slate-700">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-[10px]">✓</span>
                    {c.label}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm text-slate-600">Grab a few photos when you can — storefront, interior, work examples, and your logo.</p>
          )}
          <Tip>Square 1080×1080 works best for your logo. Natural daylight photos outperform filters.</Tip>
        </>
      ),
    },
    {
      title: "Verify your listing",
      why: "Verification is Google's proof you're real — your profile can't go live without it.",
      content: <VerificationHelper verifySel={progress.verifySel} toggleVerify={toggleVerify} />,
    },
    {
      title: "Go live 🎉",
      content: (
        <>
          <p className="text-sm text-slate-600 leading-relaxed">
            Once Google confirms your verification, your profile goes live on Google Search and Maps — usually within a few days.
            Final checklist:
          </p>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
            {["Name, category and address pasted", "Phone, website and hours set", "Description added", "Photos uploaded", "Verification completed"].map((t) => (
              <li key={t} className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-[10px]">✓</span> {t}
              </li>
            ))}
          </ul>

          {/* Reviews callout — right where they finish, at their most motivated moment */}
          <div className="mt-5 rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-emerald-50 p-4">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-xl bg-white border border-blue-100 flex items-center justify-center">
                <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
              </div>
              <div className="flex-1">
                <div className="font-heading text-sm font-semibold text-slate-900">Your listing is live — now start collecting reviews.</div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Profiles with 5+ reviews get roughly <strong>5× more clicks</strong> than ones with none (Google). Being on Google is step one — reviews are what actually get people to choose you. Ask every happy customer or visitor to leave one; a printable QR code makes it effortless.
                </p>
                {onOpenReviews && (
                  <button
                    type="button"
                    onClick={onOpenReviews}
                    data-testid="assistant-open-reviews"
                    className="mt-3 inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-slate-700 transition-colors"
                  >
                    Open my Review Kit <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <Tip>Check your profile weekly for reviews and questions — replying fast boosts your ranking and earns trust.</Tip>
        </>
      ),
    },
  ];

  const doneCount = steps.filter((_, i) => progress.done[i]).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const firstOpen = steps.findIndex((_, i) => !progress.done[i]);
  const [openStep, setOpenStep] = useState(firstOpen === -1 ? null : firstOpen);
  const allDone = doneCount === steps.length;

  const markDone = (i, value) => {
    setProgress((p) => ({ ...p, done: { ...p.done, [i]: value } }));
    if (value) {
      const next = steps.findIndex((_, j) => j > i && !progress.done[j]);
      setOpenStep(next === -1 ? null : next);
    }
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="publish-assistant">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-blue-600" />
        <h2 className="font-heading text-lg font-semibold">Publish Assistant</h2>
      </div>
      <p className="text-sm text-slate-500 leading-relaxed">
        Your answers follow you through every step — no retyping, ever. Just tap Copy, paste into Google, and check it off.
      </p>

      {/* Progress */}
      <div className="mt-5 mb-6">
        <div className="flex items-center justify-between text-xs font-medium mb-2">
          <span className="text-slate-600" data-testid="assistant-progress-label">
            {allDone ? "All steps complete!" : `Step ${Math.min(firstOpen + 1, 10)} of ${steps.length} — ${pct}% published`}
          </span>
          <span className="text-slate-400">{doneCount}/{steps.length}</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 via-green-500 to-amber-400 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {allDone && (
        <div className="mb-6 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-blue-50 p-6 text-center" data-testid="assistant-complete-banner">
          <PartyPopper className="w-8 h-8 mx-auto text-emerald-500" />
          <div className="font-heading text-xl font-semibold mt-2">{b.name || "Your listing"} is on Google!</div>
          <p className="text-sm text-slate-600 mt-1">Your profile is out there working for you. Come back anytime — your roadmap is saved.</p>
        </div>
      )}

      {/* Steps */}
      <div className="space-y-2.5">
        {steps.map((s, i) => {
          const isDone = !!progress.done[i];
          const isOpen = openStep === i;
          return (
            <div
              key={i}
              className={`rounded-2xl border transition-colors ${isOpen ? "border-slate-300 bg-white" : isDone ? "border-emerald-100 bg-emerald-50/40" : "border-slate-200 bg-slate-50/50"}`}
              data-testid={`assistant-step-${i + 1}`}
            >
              <button
                type="button"
                onClick={() => setOpenStep(isOpen ? null : i)}
                data-testid={`assistant-step-${i + 1}-header`}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
              >
                <span className={`shrink-0 w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center ${isDone ? "bg-emerald-500 text-white" : "bg-slate-900 text-white"}`}>
                  {isDone ? <Check className="w-4 h-4" /> : i + 1}
                </span>
                <span className={`flex-1 font-medium text-sm ${isDone ? "text-slate-500" : "text-slate-900"}`}>{s.title}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pl-[3.5rem]">
                  {s.why && (
                    <div className="text-xs text-slate-400 italic mb-2">Why it matters: {s.why}</div>
                  )}
                  {s.content}
                  <label className="mt-4 flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isDone}
                      onChange={(e) => markDone(i, e.target.checked)}
                      data-testid={`assistant-step-${i + 1}-done`}
                      className="w-[18px] h-[18px] rounded accent-emerald-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Mark as done</span>
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
